import React, { useState, useEffect } from 'react';
import { Grid, Box, Tab, Table, TableHead, TableBody, TableRow, TableCell, Typography, Button, IconButton } from '@mui/material';
import { useTheme } from '@mui/system';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import ApiHelper from 'utils/apiHelper';
import MainCard from 'components/Cards/MainCard';
import AddRuleDialog from './AddRuleDialog';
import EditRuleDialog from './EditRuleDialog';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';
import DeliveryReportRule from './DeliveryReportRule';
import AddReportDeliveryDialog from './AddReportDeliveryDialog';

const Automation = () => {
    const theme = useTheme();
    const { userInfo } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const manageOrganizationData = JSON.parse(localStorage.getItem('manageOrganization'));
    const [deletionRuleId, setDeletionRuleId] = useState();
    const [value, setValue] = useState('1');
    const [ruleType, setRuleType] = useState('group');
    const [groupRulesData, setGroupRulesData] = useState([]);
    const [policyRulesData, setPolicyRulesData] = useState([]);
    const [commonData, setCommonData] = useState({});
    const [ruleStatementValues, setRuleStatementValues] = useState({});
    const [showAddRuleDialog, setShowAddRuleDialog] = useState(false);
    const [showAddReportDialog, setShowAddReportDialog] = useState(false);
    const [showEditRuleDialog, setShowEditRuleDialog] = useState(false);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const [selectedRuleData, setSelectedRuleData] = useState({});
    const [selectedRuleStatements, setSelectedRuleStatements] = useState([]);

    useEffect(() => {
        getRulesData();
        getRuleStatementsData();
        getRuleStatementValuesData();
    }, []);

    const getRulesData = () => {
        setLoading(true);
        ApiHelper.get('/api/automation/rules/')
            .then((res) => {
                setGroupRulesData(res.data.group_rules);
                setPolicyRulesData(res.data.policy_rules);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the rules data');
                    setLoading(false);
                }
            });
    };

    const getRuleStatementsData = () => {
        ApiHelper.get('/api/automation/rule_statements_data/')
            .then((res) => {
                setCommonData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get rule statements data');
                }
            });
    };

    const getRuleStatementValuesData = () => {
        ApiHelper.get('/api/automation/rule_statement_values/')
            .then((res) => {
                setRuleStatementValues(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get rule statement values data');
                }
            });
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
        if (newValue === '1') {
            setRuleType('group');
        } else if (newValue === '2') {
            setRuleType('policy');
        } else if (newValue === '3') {
            setRuleType('alert_rule');
        } else {
            setRuleType('report_delivery');
        }
    };

    const handleRuleStatements = (data) => {
        let rule_statements = '';
        if (data.rule_statements.length > 0) {
            data.rule_statements.map(
                (statement) =>
                    (rule_statements += [
                        statement.device_attrib.attrib_label,
                        statement.negative === true ? 'NOT' : '',
                        statement.operator.name,
                        `'${statement.value}'`,
                        statement.connector?.name,
                        ' '
                    ].join(' '))
            );
        }
        return rule_statements;
    };

    const handleClose = () => {
        if (showAddRuleDialog) setShowAddRuleDialog(false);
        else if (showEditRuleDialog) setShowEditRuleDialog(false);
        else setShowDeleteConfirmationDialog(false);
        getRulesData();
        getRuleStatementsData();
    };

    const handleCloseReportDelivery = () => {
        window.location.reload();
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeletionRuleId(id);
    };

    const handleDeleteRule = () => {
        ApiHelper.delete(`/api/automation/rules/${deletionRuleId}/`)
            .then(() => {
                toast.success('Successfully deleted the rule.');
                setShowDeleteConfirmationDialog(false);
                getRulesData();
                getRuleStatementsData();
            })
            .catch(() => {
                toast.error('Failed to delete the rule.');
            });
    };

    const handleEditRule = (data) => {
        let tmp = [];
        let groupAttributeId = 0;
        let deviceTypeAttributeId = 0;
        const operatorLookupsData = ruleType === 'group' ? commonData.group.operator_lookups : commonData.policy.operator_lookups;
        operatorLookupsData.map((operatorLookupData) => {
            if (operatorLookupData.attrib.attribname === 'groupid') {
                groupAttributeId = operatorLookupData.attrib.attribid;
            }
            if (operatorLookupData.attrib.attribname === 'devtype') {
                deviceTypeAttributeId = operatorLookupData.attrib.attribid;
            }
        });
        data.rule_statements?.map((ruleStatementData) => {
            const operatorLookup = operatorLookupsData.some((item) => item.attrib.attribid === ruleStatementData.device_attrib.attribid);
            let valueOptions = [];
            let valueTextField = true;
            let operatorOptions = commonData.operators;
            if (operatorLookup) {
                operatorOptions = [operatorLookupsData[0].theoper];
                if (ruleStatementData.device_attrib.attribid === groupAttributeId) valueOptions = ruleStatementValues.group_values;
                else if (ruleStatementData.device_attrib.attribid === deviceTypeAttributeId)
                    valueOptions = ruleStatementValues.device_type_values;
                else valueOptions = [{ name: 'Yes' }, { name: 'No' }];
                valueTextField = false;
            }
            tmp.push({
                id: ruleStatementData.statmentid,
                attribute: ruleStatementData.device_attrib.attribid,
                negative: ruleStatementData.negative,
                operator: ruleStatementData.operator.operatorid,
                value: ruleStatementData.value,
                connector: ruleStatementData.connector ? ruleStatementData.connector.connectorid : '1',
                disabledConnector: !ruleStatementData.connector?.connectorid,
                operatorOptions: operatorOptions,
                valueTextField: valueTextField,
                valueOptions: valueOptions
            });
        });
        setSelectedRuleStatements(tmp);
        setShowEditRuleDialog(true);
        setSelectedRuleData(data);
    };

    if (!groupRulesData || !policyRulesData) {
        return null;
    } else {
        return (
            <MainCard title="Automation" cardHeaderShow={false}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>
                                    {manageOrganizationData?.orgid ? manageOrganizationData.name : userInfo.user.orgid.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                {(ruleType === 'group' || ruleType === 'policy') && (
                                    <Button
                                        sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                        onClick={() => setShowAddRuleDialog(true)}
                                        startIcon={<Add />}
                                    >
                                        Add Rule
                                    </Button>
                                )}
                                {ruleType === 'report_delivery' && (
                                    <Button
                                        sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                        onClick={() => setShowAddReportDialog(true)}
                                        startIcon={<Add />}
                                    >
                                        Add Report Delivery
                                    </Button>
                                )}
                                {showAddRuleDialog && (
                                    <AddRuleDialog
                                        ruleType={ruleType}
                                        data={commonData}
                                        onClose={handleClose}
                                        ruleStatementValues={ruleStatementValues}
                                        onCloseCancel={() => setShowAddRuleDialog(false)}
                                    />
                                )}
                                {showAddReportDialog && (
                                    <AddReportDeliveryDialog
                                        onClose={handleCloseReportDelivery}
                                        onCloseCancel={() => setShowAddReportDialog(false)}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Box sx={{ width: '100%', typography: 'body1' }}>
                            <TabContext value={value}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <TabList onChange={handleChange} indicatorColor="secondary">
                                        <Tab label="Group Rules" value="1" />
                                        <Tab label="Policy Rules" value="2" />
                                        {/*<Tab label="Alert Rules" value="3" />*/}
                                        {userInfo.admin_permissions.root_admin && <Tab label="Report Delivery" value="4" />}
                                    </TabList>
                                </Box>
                                <TabPanel value="1">
                                    {loading === true ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    ) : groupRulesData.length === 0 ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center' }}>No Group Rules Data</Typography>
                                    ) : (
                                        <Table sx={{ minWidth: 700 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rule Name</TableCell>
                                                    <TableCell>Rule Description</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell>Order</TableCell>
                                                    <TableCell>Group</TableCell>
                                                    <TableCell>Rule</TableCell>
                                                    <TableCell>Options</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {groupRulesData
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((data, key) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{data.name}</TableCell>
                                                            <TableCell>{data.description}</TableCell>
                                                            <TableCell>{data.enabled === true ? 'YES' : 'NO'}</TableCell>
                                                            <TableCell>{data.order}</TableCell>
                                                            <TableCell>{data.groupid.name}</TableCell>
                                                            <TableCell>{handleRuleStatements(data)}</TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <IconButton size="small" onClick={() => handleEditRule(data)}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteConfirmation(data.ruleid)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {showEditRuleDialog && (
                                                    <EditRuleDialog
                                                        ruleType={ruleType}
                                                        onClose={handleClose}
                                                        onCloseCancel={() => setShowEditRuleDialog(false)}
                                                        commonData={commonData}
                                                        ruleData={selectedRuleData}
                                                        ruleStatementValues={ruleStatementValues}
                                                        ruleStatementsData={selectedRuleStatements}
                                                    />
                                                )}
                                                {showDeleteConfirmationDialog && (
                                                    <DeleteConfirmationDialog
                                                        name="group rule"
                                                        onClose={() => setShowDeleteConfirmationDialog(false)}
                                                        onYes={handleDeleteRule}
                                                    />
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TabPanel>
                                <TabPanel value="2">
                                    {loading === true ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                            Please wait...
                                        </Typography>
                                    ) : policyRulesData.length === 0 ? (
                                        <Typography sx={{ mt: 3, textAlign: 'center' }}>No Policy Rules Data</Typography>
                                    ) : (
                                        <Table sx={{ minWidth: 700 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Rule Name</TableCell>
                                                    <TableCell>Rule Description</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell>Order</TableCell>
                                                    <TableCell>Policy</TableCell>
                                                    <TableCell>Rule</TableCell>
                                                    <TableCell>Options</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {policyRulesData
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((data, key) => (
                                                        <TableRow key={key}>
                                                            <TableCell>{data.name}</TableCell>
                                                            <TableCell>{data.description}</TableCell>
                                                            <TableCell>{data.enabled === true ? 'YES' : 'NO'}</TableCell>
                                                            <TableCell>{data.order}</TableCell>
                                                            <TableCell>{data.policyid.name}</TableCell>
                                                            <TableCell>{handleRuleStatements(data)}</TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <IconButton size="small" onClick={() => handleEditRule(data)}>
                                                                        <Edit fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteConfirmation(data.ruleid)}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                {showEditRuleDialog && (
                                                    <EditRuleDialog
                                                        ruleType={ruleType}
                                                        onClose={handleClose}
                                                        onCloseCancel={() => setShowEditRuleDialog(false)}
                                                        commonData={commonData}
                                                        ruleData={selectedRuleData}
                                                        ruleStatementValues={ruleStatementValues}
                                                        ruleStatementsData={selectedRuleStatements}
                                                    />
                                                )}
                                                {showDeleteConfirmationDialog && (
                                                    <DeleteConfirmationDialog
                                                        name="policy rule"
                                                        onClose={() => setShowDeleteConfirmationDialog(false)}
                                                        onYes={handleDeleteRule}
                                                    />
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                                </TabPanel>
                                <TabPanel value="3">
                                    <Typography>This tab will allow for creating/editing rules about when to alert users.</Typography>
                                </TabPanel>
                                <TabPanel value="4">
                                    <DeliveryReportRule />
                                </TabPanel>
                            </TabContext>
                        </Box>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default Automation;
